import axios from 'axios'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || ''

const api = axios.create({
    baseURL: API_BASE_URL,
    timeout: 600000, // 10分钟，允许长时间的视频下载和合并
    headers: {
        'Content-Type': 'application/json'
    }
})

// 请求拦截器 - 添加token
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('auth_token')
        if (token) {
            config.headers.Authorization = `Bearer ${token}`
        }
        return config
    },
    (error) => {
        return Promise.reject(error)
    }
)

// 响应拦截器 - 处理错误
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            // Token过期或无效，清除登录状态
            localStorage.removeItem('auth_token')
            localStorage.removeItem('user_info')
            
            // 触发登录要求事件
            const event = new CustomEvent('login-required', {
                detail: { message: error.response?.data?.detail || '请先登录后再进行操作' }
            })
            window.dispatchEvent(event)
        } else if (error.response?.status === 402) {
            // 积分不足
            const event = new CustomEvent('points-insufficient', { 
                detail: { message: error.response?.data?.detail || '积分不足，请充值' }
            })
            window.dispatchEvent(event)
        }
        return Promise.reject(error)
    }
)

export interface RegisterRequest {
    username: string
    password: string
    email: string
    email_code: string
    confirm_password?: string
}

export interface LoginRequest {
    username: string
    password: string
}

export interface PhoneLoginRequest {
    phone: string
    code: string
}

export interface SendCodeRequest {
    phone: string
}

export interface WeChatLoginRequest {
    code: string
    state?: string
}

export interface AuthResponse {
    access_token: string
    token_type: string
    user: {
        id: number
        username: string
        email?: string
        phone?: string
        avatar?: string
        nickname?: string
    }
}

export interface UserInfo {
    id: number
    username: string
    email?: string
    phone?: string
    avatar?: string
    nickname?: string
    created_at: string
}

/**
 * 用户注册
 */
export async function register(data: RegisterRequest): Promise<AuthResponse> {
    // 后端要求确认密码，如果没有提供，我们在前端复制一份
    if (!data.confirm_password) {
        data.confirm_password = data.password
    }
    const response = await api.post('/api/auth/register', data)
    return response.data
}

/**
 * 发送邮箱验证码
 */
export async function sendEmailCode(data: { email: string }): Promise<{ success: boolean; message: string }> {
    const response = await api.post('/api/auth/send-email-code', data)
    return response.data
}

/**
 * 账号密码登录
 */
export async function login(data: LoginRequest): Promise<AuthResponse> {
    const response = await api.post('/api/auth/login', data)
    return response.data
}

/**
 * 发送手机验证码
 */
export async function sendVerificationCode(data: SendCodeRequest): Promise<{ success: boolean; message: string; code?: string }> {
    const response = await api.post('/api/auth/send-code', data)
    return response.data
}

/**
 * 手机验证码登录
 */
export async function phoneLogin(data: PhoneLoginRequest): Promise<AuthResponse> {
    const response = await api.post('/api/auth/login/phone', data)
    return response.data
}

/**
 * 微信扫码登录
 */
export async function wechatLogin(data: WeChatLoginRequest): Promise<AuthResponse> {
    const response = await api.post('/api/auth/login/wechat', data)
    return response.data
}

/**
 * 获取当前用户信息
 */
export async function getCurrentUser(): Promise<UserInfo> {
    const response = await api.get('/api/auth/me')
    return response.data
}

/**
 * 登出
 */
export async function logout(): Promise<void> {
    await api.post('/api/auth/logout')
    localStorage.removeItem('auth_token')
    localStorage.removeItem('user_info')
}

/**
 * 保存登录信息
 */
export function saveAuthInfo(authResponse: AuthResponse) {
    localStorage.setItem('auth_token', authResponse.access_token)
    localStorage.setItem('user_info', JSON.stringify(authResponse.user))
}

/**
 * 获取保存的用户信息
 */
export function getSavedUserInfo() {
    const userStr = localStorage.getItem('user_info')
    if (userStr) {
        try {
            return JSON.parse(userStr)
        } catch {
            return null
        }
    }
    return null
}

/**
 * 检查是否已登录
 */
export function isLoggedIn(): boolean {
    return !!localStorage.getItem('auth_token')
}

export default api
